import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthLayout } from "@/components/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * /reset-password?token=...
 *
 * Three states:
 *   1. validating — spinner while the page-load gate runs.
 *   2. invalid    — token missing, expired, consumed, invalidated, or
 *                   referring to a missing user. Single CTA back to
 *                   /forgot-password to request a new link.
 *   3. valid      — render the new-password + confirm form (+ TOTP code
 *                   field when ``requires_2fa: true``).
 *
 * After a successful POST → render a final success state with a
 * "Sign in with your new password" link. Do NOT auto-login (per spec).
 */
export default function ResetPasswordPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token") || "";

    const [validating, setValidating] = useState(true);
    const [validation, setValidation] = useState(null);
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [totpCode, setTotpCode] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [done, setDone] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!token) {
                setValidation({ valid: false, reason: "not_found" });
                setValidating(false);
                return;
            }
            try {
                const { data } = await api.get(
                    `/auth/reset-password/validate`,
                    { params: { token } },
                );
                if (!cancelled) setValidation(data);
            } catch (err) {
                if (!cancelled) {
                    setValidation({ valid: false, reason: "not_found" });
                    toast.error(formatApiError(err));
                }
            } finally {
                if (!cancelled) setValidating(false);
            }
        })();
        return () => { cancelled = true; };
    }, [token]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (newPassword !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }
        if (newPassword.length < 8) {
            setError("Password must be at least 8 characters.");
            return;
        }
        if (validation?.requires_2fa && !(totpCode || "").trim()) {
            setError("Two-factor code required.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/auth/reset-password", {
                token,
                new_password: newPassword,
                ...(validation?.requires_2fa ? { totp_code: totpCode.trim() } : {}),
            });
            setDone(true);
            toast.success("Password updated. Sign in with your new password.");
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    if (validating) {
        return (
            <AuthLayout
                eyebrow="Reset password"
                title="Verifying your link…"
                subtitle="One moment while we confirm this reset link is still valid."
            >
                <div
                    className="flex items-center justify-center py-10 text-slate-500 text-sm"
                    data-testid="reset-password-validating"
                >
                    Checking link…
                </div>
            </AuthLayout>
        );
    }

    if (done) {
        return (
            <AuthLayout
                eyebrow="Reset password"
                title="Password updated"
                subtitle="Your password has been changed. All previous sessions have been signed out for security."
            >
                <div
                    className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900"
                    data-testid="reset-password-success-banner"
                >
                    You can now sign in with your new password.
                </div>
                <Button
                    type="button"
                    onClick={() => navigate("/login", { replace: true })}
                    data-testid="reset-password-go-to-login"
                    className="w-full bg-blue-900 hover:bg-blue-800 text-white h-11 mt-5"
                >
                    Sign in with your new password
                </Button>
            </AuthLayout>
        );
    }

    if (!validation?.valid) {
        const reasonCopy = {
            expired: "This reset link has expired. Reset links are valid for 60 minutes.",
            consumed: "This reset link has already been used. Each link works only once.",
            invalidated: "This reset link has been replaced by a newer request. Use the latest email we sent you.",
            not_found: "This reset link is invalid. Double-check that you copied the full URL.",
        };
        const reason = reasonCopy[validation?.reason] || reasonCopy.not_found;
        return (
            <AuthLayout
                eyebrow="Reset password"
                title="Link not valid"
                subtitle="We couldn't verify this reset link. Request a fresh one to continue."
                footer={
                    <Link
                        to="/login"
                        data-testid="reset-password-back-to-login"
                        className="font-medium text-blue-900 hover:underline"
                    >
                        Back to sign in
                    </Link>
                }
            >
                <div
                    className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
                    data-testid="reset-password-invalid-banner"
                >
                    {reason}
                </div>
                <Button
                    type="button"
                    onClick={() => navigate("/forgot-password", { replace: true })}
                    data-testid="reset-password-request-new"
                    className="w-full bg-blue-900 hover:bg-blue-800 text-white h-11 mt-5"
                >
                    Request a new reset link
                </Button>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout
            eyebrow="Reset password"
            title="Choose a new password"
            subtitle={
                validation.user_email_masked
                    ? `Setting a new password for ${validation.user_email_masked}.`
                    : "Set a new password for your account."
            }
            footer={
                <Link
                    to="/login"
                    data-testid="reset-password-back-to-login"
                    className="font-medium text-blue-900 hover:underline"
                >
                    Back to sign in
                </Link>
            }
        >
            <form onSubmit={onSubmit} className="space-y-5" data-testid="reset-password-form">
                <div className="space-y-2">
                    <Label htmlFor="reset-new-password">New password</Label>
                    <Input
                        id="reset-new-password"
                        data-testid="reset-password-new-input"
                        type="password"
                        autoComplete="new-password"
                        required
                        minLength={8}
                        maxLength={128}
                        value={newPassword}
                        onChange={(ev) => setNewPassword(ev.target.value)}
                        placeholder="••••••••"
                    />
                    <p className="text-xs text-slate-500">At least 8 characters.</p>
                </div>
                <div className="space-y-2">
                    <Label htmlFor="reset-confirm-password">Confirm password</Label>
                    <Input
                        id="reset-confirm-password"
                        data-testid="reset-password-confirm-input"
                        type="password"
                        autoComplete="new-password"
                        required
                        minLength={8}
                        maxLength={128}
                        value={confirmPassword}
                        onChange={(ev) => setConfirmPassword(ev.target.value)}
                        placeholder="••••••••"
                    />
                </div>
                {validation.requires_2fa && (
                    <div className="space-y-2" data-testid="reset-password-2fa-section">
                        <Label htmlFor="reset-totp">Two-factor code</Label>
                        <Input
                            id="reset-totp"
                            data-testid="reset-password-totp-input"
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            required
                            maxLength={10}
                            value={totpCode}
                            onChange={(ev) => setTotpCode(ev.target.value)}
                            placeholder="123456"
                        />
                        <p className="text-xs text-slate-500">
                            Enter the 6-digit code from your authenticator app.
                        </p>
                    </div>
                )}
                {error && (
                    <div
                        data-testid="reset-password-error"
                        className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 border border-red-100"
                    >
                        {error}
                    </div>
                )}
                <Button
                    type="submit"
                    data-testid="reset-password-submit-button"
                    disabled={submitting}
                    className="w-full bg-blue-900 hover:bg-blue-800 text-white h-11"
                >
                    {submitting ? "Updating…" : "Set new password"}
                </Button>
            </form>
        </AuthLayout>
    );
}
